import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getCommaSeparatedValues } from "@WhiteCap/Common/Utillities/utils";
import AllJobSitesModal from "@WhiteCap/Components/OSNSubscriptions/JobSitesModal";
import loadAccountDetails, {
    GetAccountDetailsApiParameter,
} from "@WhiteCap/Handlers/AccountDetails/LoadAccountDetails";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    accountInfoSectionTitle = "accountInfoSectionTitle",
    manageJobSiteSectionTitle = "manageJobSiteSectionTitle",
}


interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.accountInfoSectionTitle]: string;
        [fields.manageJobSiteSectionTitle]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    const currentSubscriptionCustomerId = state.WhiteCap.subscriptionsDashboardData.currentSubscriptionCustomerId;
    const subscriptionsData =
        state.WhiteCap.subscriptionsDashboardData.osnType === "accountLevel"
            ? state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions
            : state.WhiteCap.subscriptionsDashboardData?.jobSiteSubscriptionsById[currentSubscriptionCustomerId];
    const totalJobSites = state.WhiteCap.subscriptionsDashboardData.accountLevelSubscriptions.totalSites;

    return {
        session: state.context.session,
        billTosById: state.data.billTos.byId,
        shipTosById: state.data.shipTos.byId,
        notificationType: state.WhiteCap.subscriptionsDashboardData.osnType,
        totalJobSites,
        subscriptionsData,
        accountDetails: state.WhiteCap.accountInformationStore?.AccountInformation,
        isLoadingAccountInfo: state.WhiteCap.accountInformationStore?.isLoading,
    };
};
const mapDispatchToProps = {
    loadAccountDetails,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ManageSubscriptionsStyles {
    title?: TypographyProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    infoSection?: InjectableCss;
    sectionTitle?: TypographyProps;
    billTo?: TypographyProps;
    view?: TypographyProps;
    jobSitesWrapper?: InjectableCss;
    manage?: TypographyProps;
    loadingAnimation?: TypographyPresentationProps;
}

export const manageSubscriptionsStyles: ManageSubscriptionsStyles = {
    title: {
        weight: 325,
        variant: "h2",
        css: css`
            font-style: normal;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 14px;
                            margin: 13px 0;
                        `,
                        null,
                        css`
                            font-size: 18px;
                            margin: 19px 0;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            background: #F7F8F9;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            gap: 10px;
                            margin-bottom: 45px;
                            flex-direction: column;
                            padding: 18px 14px;
                        `,
                        null,
                        css`
                            gap: 82px;
                            margin-bottom: 65px;
                            flex-direction: row;
                            padding: 10px 25px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    sectionTitle: {
        size: 10,
        weight: 325,
        lineHeight: "20px",
        css: css``,
    },
    billTo: {
        size: 12,
        weight: 450,
        lineHeight: "20px",
        css: css`
            text-transform: none;
        `,
    },
    view: {
        color: "text.link",
        size: 12,
        weight: 450,
        lineHeight: "20px",
        css: css`
            cursor: pointer;
        `,
    },
    jobSitesWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 11px;
            .jobsite_address {
                font-weight: 325;
            }
        `,
    },
    manage: {
        color: "text.link",
        size: 12,
        weight: 325,
        lineHeight: "20px",
        css: css`
            cursor: pointer;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 7px;
                        `,
                        null,
                        css`
                            margin-top: 4px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    loadingAnimation:
    {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
};

const styles = manageSubscriptionsStyles;

const ManageSubscriptions: FC<Props> = ({
    fields,
    session,
    billTosById,
    shipTosById,
    notificationType,
    totalJobSites,
    subscriptionsData,
    loadAccountDetails,
    accountDetails,
    isLoadingAccountInfo
}) => {
    if (!session.billToId || !session.shipToId) {
        return null;
    }
    const [isOpenViewAllModal, setIsOpenViewAllModal] = useState(false);
    const [isOpenManageIndividualJobSiteModal, setIsOpenManageIndividualJobSiteModal] = useState(false);
    const dispatch = useDispatch();
    const billToId = session.billToId;
    const billToDetails = billTosById[billToId];

    useEffect(() => {
        if (!accountDetails?.customerName && !isLoadingAccountInfo) {
            loadAccountDetails({} as GetAccountDetailsApiParameter);
        }
    });

    const changeOsnTypeToAccountLevel = () => {
        dispatch({
            type: "Custom/SubscriptionDashboard/ModifyOsnTypeSelectionModalOpenState",
            value: true,
        });
    };

    const jobSiteAddress =
        getCommaSeparatedValues([
            subscriptionsData?.address1,
            subscriptionsData?.address2,
            subscriptionsData?.city,
            subscriptionsData?.state,
            subscriptionsData?.postalCode,
        ])?.toLocaleLowerCase() || "";

    const customerName = accountDetails?.customerName ? `| ${accountDetails?.customerName}` : "";

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.title}>{fields.title}</Typography>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.infoSection}>
                    <Typography {...styles.sectionTitle}>{fields.accountInfoSectionTitle}</Typography>
                    {!billToDetails?.customerNumber ? (
                        <Typography {...styles.loadingAnimation}></Typography>
                    ) : (
                        <Typography {...styles.billTo}>
                            {billToDetails?.customerNumber} {customerName}{" "}
                        </Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.infoSection}>
                    <Typography {...styles.sectionTitle}>{fields.manageJobSiteSectionTitle}</Typography>
                    {notificationType === "accountLevel" ? (
                        <>
                            <StyledWrapper {...styles.jobSitesWrapper}>
                                <Typography {...styles.billTo}>All Jobsites {`(${totalJobSites})`}</Typography>
                                <Typography {...styles.view} onClick={() => setIsOpenViewAllModal(true)}>
                                    View
                                </Typography>
                            </StyledWrapper>

                            <Typography {...styles.manage} onClick={() => setIsOpenManageIndividualJobSiteModal(true)}>
                                Manage Individual Jobsite
                            </Typography>
                        </>
                    ) : (
                        <>
                            <StyledWrapper {...styles.jobSitesWrapper}>
                                <Typography {...styles.billTo}>
                                    {subscriptionsData?.companyName}-{" "}
                                    <span className="jobsite_address">{jobSiteAddress}</span>
                                </Typography>
                                <Typography
                                    {...styles.view}
                                    onClick={() => setIsOpenManageIndividualJobSiteModal(true)}
                                >
                                    Change
                                </Typography>
                            </StyledWrapper>

                            <Typography {...styles.manage} onClick={() => changeOsnTypeToAccountLevel()}>
                                Manage All Jobsites
                            </Typography>
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {isOpenViewAllModal && (
                <AllJobSitesModal
                    isModalOpen={isOpenViewAllModal}
                    closeModalHandler={() => setIsOpenViewAllModal(false)}
                    viewOnly={true}
                />
            )}
            {isOpenManageIndividualJobSiteModal && (
                <AllJobSitesModal
                    isModalOpen={isOpenManageIndividualJobSiteModal}
                    closeModalHandler={() => setIsOpenManageIndividualJobSiteModal(false)}
                    viewOnly={false}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ManageSubscriptions),
    definition: {
        group: "My Account",
        displayName: "Manage Subscriptions",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.accountInfoSectionTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.manageJobSiteSectionTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
